// CLI BL621 — `gridctl visibility` group (Decision 041 + 042).
//
// `visibility` is the sole verb for an entity's visibility scope. Decision 041
// locks a four-mode model:
//
//   private        only the creator
//   authenticated  any signed-in CloudGrid user (network-wide)
//   grid           grid members in at least one listed space
//                  (visibility_spaces; ['everyone'] = the whole grid)
//   link           anyone with the URL
//                  (link_indexed: false = not crawlable, true = indexable)
//
// Subcommands (Decision 042 §"Mode 2"):
//
//   visibility set <entity> <mode> [--space <slug>...] [--indexed]
//   visibility add-space <entity> <slug>
//   visibility remove-space <entity> <slug>
//   visibility show <entity>
//
// All four resolve the entity slug from the cwd `cloudgrid.yaml` when the
// `<entity>` argument is omitted (folder-rooted ergonomics, like `git`).
//
// The write surface is realm-scoped (BL634): `PATCH /api/v2/entities/:id/visibility`
// for runtimes and `PATCH /api/v2/inspirations/:id/visibility` for inspirations.
// Both accept a slug *or* UUID for `:id` and share the four-mode body shape
// (inspiration-api.md §5c / §5d). The verbs resolve the realm first (the same
// runtime→inspiration fall-through `show` uses) and route to the matching
// endpoint. Reads use the realm-scoped detail endpoints (`/runtimes/:slug`,
// `/inspirations/:slug`), which carry the sparse `visibility_spaces` /
// `link_indexed` fields.

import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { Command } from 'commander';
import { parse as parseYaml } from 'yaml';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { CloudGridError } from '../utils.js';
import { handleV2Error } from '../handle-error.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';

// The four visibility-scope modes (Decision 041). Mirrors the server's
// `VISIBILITY_SCOPE_MODES` (packages/shared/src/v1-types.ts); the deprecated
// `org` value is intentionally absent — it is rejected on the write path.
const VISIBILITY_MODES = ['private', 'authenticated', 'grid', 'link'] as const;
type VisibilityMode = (typeof VISIBILITY_MODES)[number];

/** The whole-grid sentinel space slug (the default grid space, Decision 042). */
const EVERYONE = 'everyone';

function parseMode(input: string): VisibilityMode {
  const v = input.trim().toLowerCase();
  if ((VISIBILITY_MODES as readonly string[]).includes(v)) {
    return v as VisibilityMode;
  }
  if (v === 'org') {
    throw new CloudGridError(
      "The 'org' visibility is deprecated. Use 'grid' (visible to everyone in the grid).",
    );
  }
  throw new CloudGridError(
    `Unknown visibility '${input}'. Allowed values: ${VISIBILITY_MODES.join(', ')}.`,
  );
}

/** Trim + lowercase a space slug; reject an empty value before contacting the API. */
function normalizeSpaceSlug(input: string): string {
  const v = input.trim().toLowerCase();
  if (v === '') {
    throw new CloudGridError('A space slug is required.');
  }
  return v;
}

function dedupe(slugs: string[]): string[] {
  return [...new Set(slugs)];
}

// ── Shared read shape ────────────────────────────────────────────────────────

/** Detail-endpoint fields the visibility verbs read. */
interface ScopeLookup {
  entity_id: string;
  name: string;
  display_name?: string;
  url?: string;
  visibility?: string;
  visibility_spaces?: string[];
  link_indexed?: boolean;
}

interface ResolvedScope extends ScopeLookup {
  realm: 'runtime' | 'inspiration';
}

/**
 * Resolve a slug to its current visibility scope, trying the runtime realm
 * first and falling through to the inspiration realm on a 404 — the same
 * resolution chain `rename` and the pre-BL621 `visibility get` used. The detail
 * responses carry the sparse `visibility_spaces` / `link_indexed` fields per
 * contract v2.3.0 §5c.
 */
async function fetchScope(client: IV2Client, slug: string): Promise<ResolvedScope> {
  let runtime: ScopeLookup | null = null;
  try {
    runtime = await client.get<ScopeLookup>(
      `/api/v2/runtimes/${encodeURIComponent(slug)}`,
    );
  } catch (err) {
    if (!(err instanceof V2ApiError) || err.status !== 404) throw err;
  }
  if (runtime) return { ...runtime, realm: 'runtime' };

  let inspiration: ScopeLookup | null = null;
  try {
    inspiration = await client.get<ScopeLookup>(
      `/api/v2/inspirations/${encodeURIComponent(slug)}`,
    );
  } catch (err) {
    if (!(err instanceof V2ApiError) || err.status !== 404) throw err;
  }
  if (inspiration) return { ...inspiration, realm: 'inspiration' };

  throw new CloudGridError(
    `No runtime or inspiration named '${slug}' in your active grid.`,
  );
}

/**
 * BL634 — the realm-scoped visibility-scope PATCH endpoint. Runtimes write to
 * the entities surface; inspirations to the parallel inspirations surface. Both
 * accept a slug or UUID for `:id` and share the four-mode body shape.
 */
function visibilityPatchPath(realm: 'runtime' | 'inspiration', slug: string): string {
  const base = realm === 'inspiration' ? 'inspirations' : 'entities';
  return `/api/v2/${base}/${encodeURIComponent(slug)}/visibility`;
}

/**
 * The grid spaces currently in scope. `[]` when the entity is not grid-scoped;
 * `['everyone']` when grid with no explicit selector (the whole grid).
 */
function currentGridSpaces(scope: ResolvedScope): string[] {
  if (scope.visibility !== 'grid') return [];
  const spaces = scope.visibility_spaces;
  if (!Array.isArray(spaces) || spaces.length === 0) return [EVERYONE];
  return spaces;
}

function isWholeGrid(spaces: string[]): boolean {
  return spaces.length === 1 && spaces[0] === EVERYONE;
}

// ── The PATCH response + a normalized result ─────────────────────────────────

/** Wire shape of `PATCH /api/v2/entities/:id/visibility`. */
interface VisibilityPatchResponse {
  entity_id: string;
  name: string;
  visibility: string;
  visibility_spaces?: string[];
  link_indexed?: boolean;
  updated_at?: string;
}

interface VisibilityResult {
  entity_id: string;
  name: string;
  visibility: string;
  visibility_spaces?: string[];
  link_indexed?: boolean;
  updated_at?: string;
}

function resultFromPatch(resp: VisibilityPatchResponse): VisibilityResult {
  const out: VisibilityResult = {
    entity_id: resp.entity_id,
    name: resp.name,
    visibility: resp.visibility,
  };
  if (resp.visibility_spaces !== undefined) out.visibility_spaces = resp.visibility_spaces;
  if (resp.link_indexed !== undefined) out.link_indexed = resp.link_indexed;
  if (resp.updated_at !== undefined) out.updated_at = resp.updated_at;
  return out;
}

/** §23-voice human lines describing a visibility scope (no emoji, no `!`). */
function scopeLines(
  visibility: string,
  visibility_spaces: string[] | undefined,
  link_indexed: boolean | undefined,
): string[] {
  const lines: string[] = [];
  if (visibility === 'grid') {
    const spaces = visibility_spaces && visibility_spaces.length > 0 ? visibility_spaces : [EVERYONE];
    if (isWholeGrid(spaces)) {
      lines.push('  Spaces: everyone (the whole grid)');
    } else {
      lines.push(`  Spaces: ${spaces.join(', ')}`);
    }
  } else if (visibility === 'link') {
    lines.push(`  Indexed by search engines: ${link_indexed ? 'yes' : 'no'}`);
  }
  return lines;
}

// ── `visibility set` ─────────────────────────────────────────────────────────

export interface VisibilitySetOpts {
  /** Repeated `--space <slug>` values (grid mode). */
  spaces?: string[];
  /** `--indexed` flag (link mode). */
  indexed?: boolean;
  json?: boolean;
  output?: string;
}

export async function runVisibilitySet(
  client: IV2Client,
  slug: string,
  modeInput: string,
  opts: VisibilitySetOpts = {},
): Promise<void> {
  const mode = parseMode(modeInput);
  const out = Output.fromOpts({ json: !!opts.json, output: opts.output });

  const spaces = opts.spaces ?? [];
  if (spaces.length > 0 && mode !== 'grid') {
    throw new CloudGridError(
      `--space only applies to 'grid' visibility (got '${mode}').`,
    );
  }
  if (opts.indexed && mode !== 'link') {
    throw new CloudGridError(
      `--indexed only applies to 'link' visibility (got '${mode}').`,
    );
  }

  const body: Record<string, unknown> = { visibility: mode };
  if (mode === 'grid' && spaces.length > 0) {
    body.visibility_spaces = dedupe(spaces.map(normalizeSpaceSlug));
  } else if (mode === 'link') {
    // Always send the boolean so the wire shape is explicit; omitting it would
    // default to false server-side anyway.
    body.link_indexed = opts.indexed === true;
  }

  // BL634 — resolve the realm so we PATCH the matching surface (entities vs
  // inspirations). The detail fetch is the same runtime→inspiration fall-through
  // `show` uses.
  const scope = await fetchScope(client, slug);
  const resp = await client.patch<VisibilityPatchResponse>(
    visibilityPatchPath(scope.realm, slug),
    body,
  );
  const result = resultFromPatch(resp);

  out.json(() => result);
  out.human(() => {
    const label = result.name || slug;
    return [
      `Visibility for ${label} is now ${result.visibility}.`,
      ...scopeLines(result.visibility, result.visibility_spaces, result.link_indexed),
    ].join('\n');
  });
}

// ── `visibility add-space` / `visibility remove-space` ────────────────────────

export interface VisibilitySpaceOpts {
  json?: boolean;
  output?: string;
}

export async function runVisibilityAddSpace(
  client: IV2Client,
  slug: string,
  spaceInput: string,
  opts: VisibilitySpaceOpts = {},
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, output: opts.output });
  const space = normalizeSpaceSlug(spaceInput);

  const scope = await fetchScope(client, slug);
  const current = currentGridSpaces(scope);
  // Adding a specific space narrows from the whole grid, so drop the `everyone`
  // sentinel — keeping it would make the specific-space scope meaningless.
  const base = current.filter((s) => s !== EVERYONE);

  if (base.includes(space)) {
    const label = scope.display_name || scope.name || slug;
    out.human(() => `Space '${space}' is already in the visibility scope of ${label}.`);
    out.json(() => ({
      entity_id: scope.entity_id,
      name: scope.name,
      visibility: 'grid',
      visibility_spaces: base,
      changed: false,
    }));
    return;
  }

  const newSpaces = dedupe([...base, space]);
  const resp = await client.patch<VisibilityPatchResponse>(
    visibilityPatchPath(scope.realm, slug),
    { visibility: 'grid', visibility_spaces: newSpaces },
  );
  const result = resultFromPatch(resp);

  out.json(() => ({ ...result, changed: true }));
  out.human(() => {
    const label = result.name || slug;
    return [
      `Added ${space} to the visibility scope of ${label}.`,
      ...scopeLines('grid', result.visibility_spaces ?? newSpaces, undefined),
    ].join('\n');
  });
}

export async function runVisibilityRemoveSpace(
  client: IV2Client,
  slug: string,
  spaceInput: string,
  opts: VisibilitySpaceOpts = {},
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, output: opts.output });
  const space = normalizeSpaceSlug(spaceInput);

  const scope = await fetchScope(client, slug);
  const label = scope.display_name || scope.name || slug;

  if (scope.visibility !== 'grid') {
    throw new CloudGridError(
      `${label} visibility is '${scope.visibility ?? 'unknown'}', not 'grid'. There are no spaces to remove.`,
    );
  }
  const current = currentGridSpaces(scope);
  if (isWholeGrid(current)) {
    throw new CloudGridError(
      `${label} is visible to everyone in the grid; it has no specific spaces to remove.`,
    );
  }
  if (!current.includes(space)) {
    throw new CloudGridError(
      `Space '${space}' is not in the visibility scope of ${label}. Current spaces: ${current.join(', ')}.`,
    );
  }

  const newSpaces = current.filter((s) => s !== space);
  // Removing the last specific space returns the entity to the whole grid:
  // omit `visibility_spaces` so the server defaults it to ['everyone'].
  const body: Record<string, unknown> =
    newSpaces.length > 0
      ? { visibility: 'grid', visibility_spaces: newSpaces }
      : { visibility: 'grid' };

  const resp = await client.patch<VisibilityPatchResponse>(
    visibilityPatchPath(scope.realm, slug),
    body,
  );
  const result = resultFromPatch(resp);

  out.json(() => ({ ...result, changed: true }));
  out.human(() => {
    const name = result.name || slug;
    const lines = [`Removed ${space} from the visibility scope of ${name}.`];
    if (newSpaces.length === 0) {
      lines.push('  Spaces: everyone (the whole grid)');
    } else {
      lines.push(...scopeLines('grid', result.visibility_spaces ?? newSpaces, undefined));
    }
    return lines.join('\n');
  });
}

// ── `visibility show` ────────────────────────────────────────────────────────

export interface VisibilityShowOpts {
  json?: boolean;
  output?: string;
}

interface VisibilityShowResult {
  entity_id: string;
  name: string;
  display_name?: string;
  realm: 'runtime' | 'inspiration';
  visibility: string;
  visibility_spaces?: string[];
  link_indexed?: boolean;
  url?: string;
}

export async function runVisibilityShow(
  client: IV2Client,
  slug: string,
  opts: VisibilityShowOpts = {},
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, output: opts.output });
  const scope = await fetchScope(client, slug);

  const result: VisibilityShowResult = {
    entity_id: scope.entity_id,
    name: scope.name,
    realm: scope.realm,
    visibility: scope.visibility ?? 'unknown',
  };
  if (scope.display_name !== undefined) result.display_name = scope.display_name;
  if (scope.visibility_spaces !== undefined) result.visibility_spaces = scope.visibility_spaces;
  if (scope.link_indexed !== undefined) result.link_indexed = scope.link_indexed;
  if (scope.url !== undefined) result.url = scope.url;

  out.json(() => result);
  out.human(() => {
    const label = result.display_name || result.name;
    const lines = [`${label}: visibility = ${result.visibility}`];
    lines.push(...scopeLines(result.visibility, result.visibility_spaces, result.link_indexed));
    if (out.isWide && result.url) {
      lines.push(`  URL: ${result.url}`);
    }
    return lines.join('\n');
  });
}

// ── cwd-entity resolution + arg disambiguation ───────────────────────────────

/**
 * Resolve the entity slug from the cwd `cloudgrid.yaml` `name:` field, used
 * when the `<entity>` argument is omitted (folder-rooted ergonomics).
 */
function resolveCwdEntity(cwd: string = process.cwd()): string {
  const yamlPath = join(cwd, 'cloudgrid.yaml');
  if (!existsSync(yamlPath)) {
    throw new CloudGridError(
      'No entity given and no cloudgrid.yaml in the current folder. Pass an entity name, or run this from an entity folder.',
    );
  }
  let parsed: unknown;
  try {
    parsed = parseYaml(readFileSync(yamlPath, 'utf-8'));
  } catch (err) {
    throw new CloudGridError(`Could not parse cloudgrid.yaml: ${(err as Error).message}`);
  }
  const name =
    parsed && typeof parsed === 'object' ? (parsed as { name?: unknown }).name : undefined;
  if (typeof name !== 'string' || name === '') {
    throw new CloudGridError(
      'cloudgrid.yaml has no name. Pass an entity name explicitly.',
    );
  }
  return name;
}

/** `set <entity> <mode>` with the entity defaulting to the cwd entity. */
function resolveSetArgs(first: string, second?: string): { slug: string; mode: string } {
  if (second === undefined) {
    return { slug: resolveCwdEntity(), mode: first };
  }
  return { slug: first, mode: second };
}

/** `add-space|remove-space <entity> <slug>` with entity defaulting to cwd. */
function resolveSpaceArgs(first: string, second?: string): { slug: string; space: string } {
  if (second === undefined) {
    return { slug: resolveCwdEntity(), space: first };
  }
  return { slug: first, space: second };
}

function collectSpace(value: string, acc: string[]): string[] {
  acc.push(value);
  return acc;
}

// ── Commander wiring ─────────────────────────────────────────────────────────

interface SetCmdOpts {
  space?: string[];
  indexed?: boolean;
  json?: boolean;
  output?: string;
}

const setCommand = addOutputOption(
  new Command('set')
    .description('Set an entity visibility scope (private | authenticated | grid | link)')
    .argument('<entity>', 'Entity slug (defaults to the cwd entity when omitted with a mode)')
    .argument('[mode]', 'Visibility mode: private | authenticated | grid | link')
    .option('--space <slug>', 'Grid space to scope to; repeatable (grid mode)', collectSpace, [])
    .option('--indexed', 'Allow search engines to index the link (link mode)')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (entity: string, mode: string | undefined, opts: SetCmdOpts) => {
  try {
    const { slug, mode: resolvedMode } = resolveSetArgs(entity, mode);
    const client = await getV2Client();
    await runVisibilitySet(client, slug, resolvedMode, {
      spaces: opts.space ?? [],
      indexed: opts.indexed,
      json: opts.json,
      output: opts.output,
    });
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});

interface SpaceCmdOpts {
  json?: boolean;
  output?: string;
}

const addSpaceCommand = addOutputOption(
  new Command('add-space')
    .description('Add a grid space to an entity visibility scope')
    .argument('<entity>', 'Entity slug (defaults to the cwd entity when omitted)')
    .argument('[space]', 'Grid space slug to add')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (entity: string, space: string | undefined, opts: SpaceCmdOpts) => {
  try {
    const { slug, space: spaceSlug } = resolveSpaceArgs(entity, space);
    const client = await getV2Client();
    await runVisibilityAddSpace(client, slug, spaceSlug, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});

const removeSpaceCommand = addOutputOption(
  new Command('remove-space')
    .description('Remove a grid space from an entity visibility scope (the entity is not deleted)')
    .argument('<entity>', 'Entity slug (defaults to the cwd entity when omitted)')
    .argument('[space]', 'Grid space slug to remove')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (entity: string, space: string | undefined, opts: SpaceCmdOpts) => {
  try {
    const { slug, space: spaceSlug } = resolveSpaceArgs(entity, space);
    const client = await getV2Client();
    await runVisibilityRemoveSpace(client, slug, spaceSlug, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});

const showCommand = addOutputOption(
  new Command('show')
    .description('Show an entity current visibility scope')
    .argument('[entity]', 'Entity slug (defaults to the cwd entity when omitted)')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (entity: string | undefined, opts: VisibilityShowOpts) => {
  try {
    const slug = entity ?? resolveCwdEntity();
    const client = await getV2Client();
    await runVisibilityShow(client, slug, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});

export const visibilityCommand = new Command('visibility')
  .description('Set or inspect an entity visibility scope')
  .addCommand(setCommand)
  .addCommand(addSpaceCommand)
  .addCommand(removeSpaceCommand)
  .addCommand(showCommand);
